import React, { createContext, useContext, useState, useEffect, useRef } from 'react';
import { useAuth, useUser } from '@clerk/clerk-react';
import { backendFetch } from '@/lib/backend';
import { SUBSCRIPTIONS_CHANGED_EVENT } from '@/lib/app-events';
import { useOnboarding } from '@/context/OnboardingContext';
import { sanitizeOptionalText, sanitizeText } from '@/lib/sanitize';
import { toast } from 'sonner';

export type Subscription = {
  id: string;
  name: string;
  plan: string;
  price: number;
  cycle: 'Monthly' | 'Yearly';
  nextBill: string;
  status: 'active' | 'warning' | 'review' | 'paused';
  category: string;
  color: string;
  card: string;
  startedAt?: string;
  trialEndsAt?: string;
  intent?: string;
  receiptUrl?: string;
  currency?: string;
  isLifetime?: boolean;
  purchaseDate?: string;
  myShare?: number;
  splitWith?: Array<{ name: string; amount: number; email?: string }>;
  usageConfidence?: number;
  metadata?: Record<string, unknown>;
};

interface SubscriptionContextType {
  subscriptions: Subscription[];
  isLoading: boolean;
  fetchError: string | null;
  addSubscription: (sub: Subscription) => Promise<void>;
  removeSubscription: (id: string) => Promise<void>;
  updateSubscription: (id: string, updates: Partial<Subscription>) => Promise<void>;
  bulkAddSubscriptions: (subs: Subscription[]) => Promise<void>;
  bulkRemoveSubscriptions: (ids: string[]) => Promise<void>;
  bulkUpdateSubscriptions: (items: Array<{ id: string; update: Partial<Subscription> }>) => Promise<void>;
  clearData: () => Promise<void>;
  seedDemoData: () => Promise<void>;
  totalMonthlySpend: number;
  totalYearlySpend: number;
}

const SubscriptionContext = createContext<SubscriptionContextType | undefined>(undefined);

const showMutationFailure = (message: string, error: unknown) => {
  toast.error(message, {
    description: error instanceof Error ? error.message : 'Please try again.',
    duration: 5000,
  });
};

export const SubscriptionProvider = ({ children }: { children: React.ReactNode }) => {
  const { user } = useUser();
  const { isLoaded, isSignedIn } = useAuth();
  const { profile, loading: profileLoading } = useOnboarding();
  const [subscriptions, setSubscriptions] = useState<Subscription[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [fetchError, setFetchError] = useState<string | null>(null);
  const hasLoadedOnce = useRef(false);
  const lastErrorToastAt = useRef(0);

  const normalizeSubscription = (data: Record<string, unknown>, fallbackId?: string): Subscription => ({
    id: sanitizeText(fallbackId || data.id || '', 120),
    name: sanitizeText(data.name, 120),
    plan: sanitizeText(data.plan, 120),
    price: Number(data.price || 0),
    cycle: (data.cycle as Subscription['cycle']) || 'Monthly',
    nextBill: sanitizeText(data.nextBill || data.next_bill || '', 40),
    status: (data.status as Subscription['status']) || 'active',
    category: sanitizeText(data.category, 80),
    color: sanitizeText(data.color || '#86998d', 24),
    card: sanitizeText(data.card, 80),
    startedAt: sanitizeOptionalText(data.startedAt || data.started_at, 40),
    trialEndsAt: sanitizeOptionalText(data.trialEndsAt || data.trial_ends_at, 40),
    intent: sanitizeOptionalText(data.intent, 240),
    receiptUrl: sanitizeOptionalText(data.receiptUrl || data.receipt_url, 7_000_000),
    currency: data.currency ? sanitizeText(data.currency, 3) : 'USD',
    isLifetime: Boolean(data.isLifetime ?? data.is_lifetime ?? false),
    purchaseDate: data.purchaseDate || data.purchase_date ? String(data.purchaseDate || data.purchase_date) : undefined,
    myShare: data.myShare || data.my_share ? Number(data.myShare || data.my_share) : undefined,
    splitWith: Array.isArray(data.splitWith) ? data.splitWith as Subscription['splitWith'] : Array.isArray(data.split_with) ? data.split_with as Subscription['splitWith'] : undefined,
    usageConfidence: data.usageConfidence || data.usage_confidence ? Number(data.usageConfidence || data.usage_confidence) : undefined,
    metadata: typeof data.metadata === 'object' && data.metadata !== null ? data.metadata as Record<string, unknown> : undefined,
  });

  useEffect(() => {
    if (!isLoaded || profileLoading) {
      setIsLoading(true);
      return;
    }

    if (!isSignedIn || !user || !profile?.onboardingCompleted) {
      setSubscriptions([]);
      hasLoadedOnce.current = false;
      setIsLoading(false);
      setFetchError(null);
      return;
    }

    let cancelled = false;
    const syncSubscriptions = async (forceRefresh = false) => {
      try {
        setFetchError(null);
        const data = await backendFetch('/subscriptions', {
          forceRefresh,
          staleTtlMs: 10 * 60_000,
        });
        if (!cancelled) {
          const subs = (Array.isArray(data.subscriptions) ? data.subscriptions : []) as Record<string, unknown>[];
          setSubscriptions(subs.map((sub) => normalizeSubscription(sub, String(sub.id || ''))));
          hasLoadedOnce.current = true;
        }
      } catch (err: unknown) {
        if (!cancelled) {
          setSubscriptions([]);
          const msg = err instanceof Error ? err.message : 'Unable to load subscriptions';
          setFetchError(msg);
          const isSessionWarmup = msg.includes('Invalid or expired token') || msg.includes('Missing bearer token');
          const now = Date.now();
          if (!isSessionWarmup && now - lastErrorToastAt.current > 60_000) {
            lastErrorToastAt.current = now;
            toast.error('Failed to load subscriptions. Check your connection.', { duration: 5000 });
          }
        }
      } finally {
        if (!cancelled) setIsLoading(false);
      }
    };

    setIsLoading(!hasLoadedOnce.current);
    syncSubscriptions();
    const backgroundRefresh = window.setTimeout(() => syncSubscriptions(true), 300);
    const interval = window.setInterval(() => syncSubscriptions(true), 60000);
    const handleExternalMutation = () => syncSubscriptions(true);
    window.addEventListener(SUBSCRIPTIONS_CHANGED_EVENT, handleExternalMutation);
    return () => {
      cancelled = true;
      window.clearTimeout(backgroundRefresh);
      window.clearInterval(interval);
      window.removeEventListener(SUBSCRIPTIONS_CHANGED_EVENT, handleExternalMutation);
    };
  }, [isLoaded, isSignedIn, profileLoading, profile?.onboardingCompleted, user]);

  const addSubscription = async (sub: Subscription) => {
    if (!user) return;
    try {
      await backendFetch('/subscriptions', { method: 'POST', body: JSON.stringify(sub) });
      setSubscriptions((prev) => [sub, ...prev.filter((current) => current.id !== sub.id)]);
    } catch (error) {
      showMutationFailure('Unable to add subscription. Nothing was changed.', error);
      throw error;
    }
  };

  const removeSubscription = async (id: string) => {
    if (!user) return;
    try {
      await backendFetch(`/subscriptions/${id}`, { method: 'DELETE' });
      setSubscriptions((prev) => prev.filter((s) => s.id !== id));
    } catch (error) {
      showMutationFailure('Unable to remove subscription. Nothing was changed.', error);
      throw error;
    }
  };

  const updateSubscription = async (id: string, updates: Partial<Subscription>) => {
    if (!user) return;
    try {
      await backendFetch(`/subscriptions/${id}`, { method: 'PATCH', body: JSON.stringify(updates) });
      setSubscriptions((prev) => prev.map((s) => s.id === id ? { ...s, ...updates } : s));
    } catch (error) {
      showMutationFailure('Unable to update subscription. Nothing was changed.', error);
      throw error;
    }
  };

  const bulkAddSubscriptions = async (subs: Subscription[]) => {
    if (!user) return;
    try {
      await backendFetch('/subscriptions/bulk', {
        method: 'POST',
        body: JSON.stringify({ items: subs }),
      });
      setSubscriptions((prev) => {
        const addedIds = new Set(subs.map((s) => s.id));
        return [...subs, ...prev.filter((s) => !addedIds.has(s.id))];
      });
    } catch (error) {
      showMutationFailure('Bulk subscription addition failed.', error);
      throw error;
    }
  };

  const bulkRemoveSubscriptions = async (ids: string[]) => {
    if (!user) return;
    try {
      await backendFetch('/subscriptions/bulk', {
        method: 'DELETE',
        body: JSON.stringify({ ids }),
      });
      setSubscriptions((prev) => prev.filter((s) => !ids.includes(s.id)));
    } catch (error) {
      showMutationFailure('Bulk subscription deletion failed.', error);
      throw error;
    }
  };

  const bulkUpdateSubscriptions = async (items: Array<{ id: string; update: Partial<Subscription> }>) => {
    if (!user) return;
    try {
      await backendFetch('/subscriptions/bulk', {
        method: 'PATCH',
        body: JSON.stringify({ items }),
      });
      setSubscriptions((prev) =>
        prev.map((s) => {
          const match = items.find((item) => item.id === s.id);
          return match ? { ...s, ...match.update } : s;
        })
      );
    } catch (error) {
      showMutationFailure('Bulk subscription updates failed.', error);
      throw error;
    }
  };

  const clearData = async () => {
    if (!user) return;
    try {
      await backendFetch('/subscriptions', { method: 'DELETE' });
      setSubscriptions([]);
    } catch (error) {
      showMutationFailure('Unable to clear subscriptions. Nothing was changed.', error);
      throw error;
    }
  };

  const seedDemoData = async () => {
    if (!user) return;
    try {
      const { createDemoStack } = await import('@/lib/demo-stack');
      const demoStack = createDemoStack();
      await bulkAddSubscriptions(demoStack);
    } catch (error) {
      showMutationFailure('Unable to load demo stack. Nothing was changed.', error);
      throw error;
    }
  };

  const totalMonthlySpend = subscriptions.reduce((acc, sub) => {
    if (sub.isLifetime) return acc;
    const effectivePrice = typeof sub.myShare === 'number' ? sub.myShare : sub.price;
    return acc + (sub.cycle === 'Yearly' ? effectivePrice / 12 : effectivePrice);
  }, 0);

  const totalYearlySpend = totalMonthlySpend * 12;

  return (
    <SubscriptionContext.Provider value={{ 
      subscriptions,
      isLoading,
      fetchError,
      addSubscription, 
      removeSubscription,
      updateSubscription,
      bulkAddSubscriptions,
      bulkRemoveSubscriptions,
      bulkUpdateSubscriptions,
      clearData,
      seedDemoData,
      totalMonthlySpend,
      totalYearlySpend
    }}>
      {children}
    </SubscriptionContext.Provider>
  );
};

export const useSubscriptions = () => {
  const context = useContext(SubscriptionContext);
  if (!context) {
    throw new Error('useSubscriptions must be used within a SubscriptionProvider');
  }
  return context;
};
